'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import styles from './partners.module.css';


const bringSteps = [
  {
    num: '01',
    title: 'Live AI Voice Agent',
    desc: 'Deployed and serving real businesses today — not a prototype. Our voice agents handle calls, scheduling, and inquiries 24/7.',
    illus: (
      <div className={styles.illusCallWrap}>
        <div className={styles.illusCallBadge}><span className={styles.illusCallDot} /> Live · AI Voice Agent</div>
        <div className={styles.illusCallNumber}>Deployed Today</div>
        <div className={styles.illusWaveBars}>
          {[...Array(12)].map((_, i) => (
            <span key={i} className={styles.illusWaveBar} style={{ animationDelay: `${i * 0.08}s` }} />
          ))}
        </div>
      </div>
    ),
  },
  {
    num: '02',
    title: 'Enterprise GenAI & LLM',
    desc: 'Full-stack large language model application development for any enterprise use case — from document analysis to intelligent automation.',
    illus: (
      <div className={styles.illusLlmWrap}>
        <div className={styles.illusLlmTitle}>Enterprise LLM Pipeline</div>
        <div className={styles.illusLlmPrompt}>&gt; Analyze contract clause 4.2...</div>
        <div className={styles.illusLlmResponse}>Based on FAR 52.212-4, the clause requires written acceptance within...</div>
        <div className={styles.illusLlmMeta}><span>GPT-4 · Claude</span><span>~1.2s</span></div>
      </div>
    ),
  },
  {
    num: '03',
    title: 'Computer Vision & Multimodal AI',
    desc: 'Advanced vision and multimodal AI systems for complex real-world government and enterprise scenarios.',
    illus: (
      <div className={styles.illusVisionWrap}>
        <div className={styles.illusVisionTitle}>Computer Vision · Live</div>
        <div className={styles.illusVisionBox}>
          <div className={styles.illusVisionDetectA}><span className={styles.illusVisionLabel}>Person 98%</span></div>
          <div className={styles.illusVisionDetectB}><span className={styles.illusVisionLabel}>Object 91%</span></div>
          <div className={styles.illusVisionGrid} />
        </div>
      </div>
    ),
  },
  {
    num: '04',
    title: 'Fraud Detection & Cybersecurity AI',
    desc: 'AI-powered threat detection and fraud prevention systems built for enterprise and government security requirements.',
    illus: (
      <div className={styles.illusFraudWrap}>
        <div className={styles.illusFraudTitle}>Fraud Detection Alert</div>
        <div className={styles.illusFraudScore}>
          <span className={styles.illusFraudNum}>94</span>
          <span className={styles.illusFraudScoreLabel}>Risk Score</span>
        </div>
        <div className={styles.illusFraudRow}><span>Transaction</span><span className={styles.illusFraudAlert}>⚠ Flagged</span></div>
        <div className={styles.illusFraudRow}><span>Pattern</span><span>Anomaly detected</span></div>
        <div className={styles.illusFraudRow}><span>Action</span><span className={styles.illusFraudBlock}>Blocked</span></div>
      </div>
    ),
  },
  {
    num: '05',
    title: 'Custom AI for Any Industry',
    desc: 'Bespoke AI development tailored to your mission requirements and tech stack — any industry, any use case.',
    illus: (
      <div className={styles.illusCodeWrap}>
        <div className={styles.illusCodeTitle}>Custom AI Development</div>
        <div className={styles.illusCodeLine}><span className={styles.illusCodeKw}>class</span> <span className={styles.illusCodeCls}>CustomAgent</span>:</div>
        <div className={styles.illusCodeLine}>&nbsp;&nbsp;<span className={styles.illusCodeKw}>def</span> <span className={styles.illusCodeFn}>process</span>(self, input):</div>
        <div className={styles.illusCodeLine}>&nbsp;&nbsp;&nbsp;&nbsp;<span className={styles.illusCodeKw}>return</span> self.model(input)</div>
        <div className={styles.illusCodeLine}><span className={styles.illusCodeComment}># Any industry · Any stack</span></div>
        <div className={styles.illusCodeBadge}>Production Ready</div>
      </div>
    ),
  },
  {
    num: '06',
    title: 'PhD Leadership · IEEE Awarded',
    desc: '20+ years of enterprise AI experience, IEEE award-winning published research, and a PhD-led team ready to deliver.',
    illus: (
      <div className={styles.illusAwardWrap}>
        <div className={styles.illusAwardBadge}>🏆 IEEE Award-Winning</div>
        <div className={styles.illusAwardRow}><span>Leadership</span><span>PhD Team</span></div>
        <div className={styles.illusAwardRow}><span>Experience</span><span>20+ Years Enterprise AI</span></div>
        <div className={styles.illusAwardRow}><span>Research</span><span>Published · Awarded</span></div>
        <div className={styles.illusAwardRow}><span>Founded</span><span>2024 · SBA Certified</span></div>
      </div>
    ),
  },
];

export default function PartnersPage() {
  const [currentBring, setCurrentBring] = useState(0);
  const bringRef = useRef<HTMLDivElement>(null);

  const goToBring = (idx: number) => {
    const clamped = Math.max(0, Math.min(idx, bringSteps.length - 1));
    setCurrentBring(clamped);
    const vp = bringRef.current;
    if (!vp) return;
    const cards = vp.querySelectorAll('[data-bring-card]');
    cards[clamped]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  };

  return (
    <div className={styles.page}>
      <NavBar />

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <Image src="/images/partner.png" alt="Partner with NovaMind AI" fill priority className={styles.heroImage} />
        <div className={styles.heroOverlay} />
        <div className={styles.heroLayout}>
          <div className={styles.heroContent}>
            <span className={styles.heroTag}>Partner With NovaMind AI</span>
            <h1 className={styles.heroTitle}>Where Serious AI<br />Partnerships Begin.</h1>
            <p className={styles.heroSubtitle}>
              SBA-certified Small Business specializing in<br />
              production-ready Generative AI for government and enterprise.<br />
              Teaming · Subcontracting · Strategic Partnerships.
            </p>
            <div className={styles.heroBtns}>
              <Link href="/contact" className={styles.heroBtnPrimary}>Contact Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── CERTIFICATIONS STRIP ── */}
      <section className={styles.certsStrip}>
        <div className={styles.certsStripInner}>
          <span className={styles.certsStripHeading}>Verified Credentials</span>
          <div className={styles.certsStripItems}>
            <div className={styles.certItem}><span className={styles.certIcon}>✓</span><span>Small Business (SB) · SAM.gov Verified</span></div>
            <div className={styles.certDivider} />
            <div className={styles.certItem}><span className={styles.certIcon}>🔑</span><span>UEI: R5DUS83ZFUF3</span></div>
            <div className={styles.certDivider} />
            <div className={styles.certItem}><span className={styles.certIcon}>🔑</span><span>CAGE: 12MU9</span></div>
            <div className={styles.certDivider} />
            <div className={styles.certItem}><span className={styles.certIcon}>⏳</span><span>SBA 8(a) Pending</span></div>
            <div className={styles.certDivider} />
            <div className={styles.certItem}><span className={styles.certIcon}>🔒</span><span>HIPAA · SOC 2 · FedRAMP</span></div>
          </div>
        </div>
      </section>

      {/* ── WHAT WE BRING ── */}
      <section className={styles.bringSection}>
        <div className={styles.bringHeader}>
          <div className={styles.bringHeaderText}>
            <h2 className={styles.howTitle}>What We Bring to the Team.</h2>
            <p className={styles.howSubtitle}>Not prototypes — real AI deployed in production today</p>
          </div>
          <div className={styles.sliderArrows}>
            <button className={styles.arrowBtn} onClick={() => goToBring(currentBring - 1)} disabled={currentBring === 0} aria-label="Previous">‹</button>
            <button className={styles.arrowBtn} onClick={() => goToBring(currentBring + 1)} disabled={currentBring === bringSteps.length - 1} aria-label="Next">›</button>
          </div>
        </div>
        <div className={styles.sliderViewport} ref={bringRef}>
          {bringSteps.map((step, i) => (
            <div key={i} data-bring-card className={`${styles.slideCard} ${styles[`bringCard${i + 1}`]}`}>
              <span className={styles.slideNum}>{step.num}</span>
              <div className={styles.slideIllus}>{step.illus}</div>
              <div className={styles.slideText}>
                <h3 className={styles.slideTitle}>{step.title}</h3>
                <p className={styles.slideDesc}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHO WE WORK WITH ── */}
      <section className={styles.featuresSection}>
        <div className={styles.featuresInner}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>GOVERNMENT · ENTERPRISE · PRIME CONTRACTORS</span>
            <h2>Who We Work With.</h2>
            <p>We actively seek partnerships across government contracting, enterprise AI, and system integration.</p>
          </div>
          <div className={styles.featuresGrid}>

            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusContractWrap}>
                  <div className={styles.illusContractBadge}><span className={styles.illusContractDot} /> Active Solicitation</div>
                  <div className={styles.illusContractTitle}>AI Task Order RFP</div>
                  <div className={styles.illusContractRow}><span>Solicitation</span><span>W900KK-26-R-0041</span></div>
                  <div className={styles.illusContractRow}><span>Agency</span><span>Dept. of Defense</span></div>
                  <div className={styles.illusContractRow}><span>Set-Aside</span><span>Small Business</span></div>
                  <div className={styles.illusContractGreen}>✓ NovaMind Teaming</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Prime Contractors</h3>
                <p className={styles.fcSubtitle}>Pursuing government AI contracts and need a proven AI subcontractor with SB certifications.</p>
              </div>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusEnterpriseWrap}>
                  <div className={styles.illusEnterpriseTitle}>Enterprise AI Integration</div>
                  {[
                    { icon: '🤖', name: 'AI Voice Agent', status: 'Live', active: true },
                    { icon: '🧠', name: 'LLM Pipeline', status: 'Live', active: true },
                    { icon: '👁️', name: 'Computer Vision', status: 'Staging', active: false },
                    { icon: '🔒', name: 'Fraud Detection', status: 'Staging', active: false },
                  ].map((item, i) => (
                    <div key={i} className={styles.illusEnterpriseRow}>
                      <span>{item.icon}</span>
                      <span className={styles.illusEnterpriseName}>{item.name}</span>
                      <span className={item.active ? styles.illusEnterpriseActive : styles.illusEnterprisePending}>{item.status}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Enterprise Companies</h3>
                <p className={styles.fcSubtitle}>Need a specialized AI subcontractor to power your enterprise products and platforms.</p>
              </div>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusApiWrap}>
                  <div className={styles.illusApiTitle}>AI Capabilities API</div>
                  {[
                    { method: 'POST', path: '/v1/voice-agent' },
                    { method: 'POST', path: '/v1/llm/generate' },
                    { method: 'GET',  path: '/v1/vision/detect' },
                    { method: 'POST', path: '/v1/fraud/analyze' },
                  ].map((ep, i) => (
                    <div key={i} className={styles.illusApiRow}>
                      <span className={styles.illusApiMethod}>{ep.method}</span>
                      <span className={styles.illusApiPath}>{ep.path}</span>
                    </div>
                  ))}
                  <div className={styles.illusApiBadge}>✓ Production Ready</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>System Integrators</h3>
                <p className={styles.fcSubtitle}>Looking to add cutting-edge AI capabilities to your existing integration stack.</p>
              </div>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusIndustryWrap}>
                  {[
                    { icon: '🏥', name: 'Healthcare', badge: 'HIPAA Ready' },
                    { icon: '🏦', name: 'Financial', badge: 'SOC 2' },
                    { icon: '💻', name: 'Technology', badge: 'FedRAMP' },
                  ].map((item, i) => (
                    <div key={i} className={styles.illusIndustryRow}>
                      <span className={styles.illusIndustryIcon}>{item.icon}</span>
                      <span className={styles.illusIndustryName}>{item.name}</span>
                      <span className={styles.illusIndustryBadge}>{item.badge}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Regulated Industries</h3>
                <p className={styles.fcSubtitle}>Healthcare, financial, and tech firms needing HIPAA, SOC 2, and FedRAMP-ready AI.</p>
              </div>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusTeamingWrap}>
                  <div className={styles.illusTeamingTitle}>SBA 8(a) Teaming</div>
                  <div className={styles.illusTeamingRow}><span>Prime</span><span>Large Business</span></div>
                  <div className={styles.illusTeamingRow}><span>Sub</span><span>NovaMind AI (SB)</span></div>
                  <div className={styles.illusTeamingRow}><span>Contract</span><span>8(a) Set-Aside</span></div>
                  <div className={styles.illusTeamingArrow}>↕</div>
                  <div className={styles.illusTeamingBadge}>Agreement Ready</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>SBA 8(a) Teaming</h3>
                <p className={styles.fcSubtitle}>Organizations pursuing SBA 8(a) set-aside opportunities that need a certified AI partner.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── TRY LIVE AI ── */}
      <section className={styles.demoSection}>
        <div className={styles.demoHeader}>
          <span className={styles.demoEyebrow}>Try Before You Partner</span>
          <h2 className={styles.demoTitle}>Experience Our AI Firsthand.</h2>
          <p className={styles.demoSubtitle}>
            Call our live deployed agents — the same technology we bring to every partnership.
          </p>
        </div>
        <div className={styles.demoCards}>

          {/* Dental */}
          <div className={styles.demoCard}>
            <Image src="/images/dental.png" alt="Live Dental Agent" fill className={styles.demoCardBg} />
            <div className={styles.demoCardOverlay} />
            <div className={styles.demoCardContent}>
              <div className={styles.demoLivePill}><span className={styles.demoLiveDot} />LIVE</div>
              <h3 className={styles.demoCardTitle}>Live Dental Agent</h3>
              <p className={styles.demoCardDesc}>Scheduling, FAQs, and patient intake — fully deployed in a real dental practice.</p>
              <a href="tel:5599109756" className={styles.demoPhoneNum}>559-910-9756</a>
              <a href="tel:5599109756" className={styles.demoCallBtn}>Call Now</a>
            </div>
          </div>

          {/* Restaurant */}
          <div className={styles.demoCard}>
            <Image src="/images/restaurant.png" alt="Live Restaurant Agent" fill className={styles.demoCardBg} />
            <div className={styles.demoCardOverlay} />
            <div className={styles.demoCardContent}>
              <div className={styles.demoLivePill}><span className={styles.demoLiveDot} />LIVE</div>
              <h3 className={styles.demoCardTitle}>Live Restaurant Agent</h3>
              <p className={styles.demoCardDesc}>Reservation and ordering AI serving real restaurant customers every day.</p>
              <a href="tel:6183481765" className={styles.demoPhoneNum}>618-348-1765</a>
              <a href="tel:6183481765" className={styles.demoCallBtn}>Call Now</a>
            </div>
          </div>

        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.ctaSection}>
        <Image src="/images/meeting.jpg" alt="Partner with NovaMind AI" fill priority className={styles.ctaImage} />
        <div className={styles.ctaOverlay} />
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>Ready to Explore a Partnership?</h2>
          <p className={styles.ctaSubtitle}>Request our full capability statement or schedule a 30-minute demo.</p>
          <Link href="/contact" className={styles.ctaBtn}>Contact Us</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
